import { call, select, takeLatest } from 'redux-saga/effects';
import * as api from '@api/siteUserQueryManagement';
import { runToast, showErrorToast } from '../tools';
import { actions } from './siteUserQueries.slice';
import { Resp, dispatch } from '../helpers';
import {
  SiteUserQueriesFetchAction,
  SiteUserQueriesFilters,
  UpdateCurrentQueryAction,
} from './siteUserQueries.types';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@restore/store';
import { PaginationAndSortType } from '@restore/paginationAndSort/paginationAndSort.types';

export function* fetchSiteUserQueriesWorker(
  action: SiteUserQueriesFetchAction
) {
  try {
    const localFilters = (yield select(
      (state: RootState) => state.siteUserQueries?.filters || {}
    )) as SiteUserQueriesFilters;
    const pagSort = (yield select(
      (state: RootState) =>
        state.paginationAndSort?.lists?.siteUserQueries || {
          currentPage: 1,
          currentPageSize: 10,
        }
    )) as PaginationAndSortType;
    const filters = {
      ...localFilters,
      page: pagSort.currentPage,
      size: pagSort.currentPageSize,
      sort: pagSort.sortBy,
    };
    const { clinicalTrialId, siteId } = action.payload as {
      clinicalTrialId: string;
      siteId: string;
    };
    const response: Resp<typeof api.getSiteUserQueries> = yield call(
      api.getSiteUserQueries,
      clinicalTrialId,
      siteId,
      filters
    );
    yield dispatch(actions.fetchSiteUserQueriesSuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch queries');
    yield dispatch(actions.fetchSiteUserQueriesFailure(error as Error));
  }
}

export function* fetchCurrentQueryWorker(
  action: PayloadAction<{
    clinicalTrialId: string;
    queryId: string;
    siteId: string;
  }>
) {
  try {
    const { clinicalTrialId, queryId, siteId } = action.payload;
    const response: Resp<typeof api.getSiteUserQueryById> = yield call(
      api.getSiteUserQueryById,
      queryId,
      clinicalTrialId,
      siteId
    );
    yield dispatch(actions.fetchCurrentQuerySuccess(response.data));
  } catch (error) {
    yield* showErrorToast(error, 'Failed to fetch query');
    yield dispatch(actions.fetchCurrentQueryFailure(error as Error));
  }
}

export function* updateCurrentQueryWorker(action: UpdateCurrentQueryAction) {
  try {
    const { clinicalTrialId, siteId, queryId, updateData, refreshList } =
      action.payload;
    yield call(
      api.updateSiteUserQueryById,
      queryId,
      clinicalTrialId,
      siteId,
      updateData
    );
    const fetchResponse: Resp<typeof api.getSiteUserQueryById> = yield call(
      api.getSiteUserQueryById,
      queryId,
      clinicalTrialId,
      siteId
    );
    yield dispatch(actions.updateCurrentQuerySuccess(fetchResponse.data));
    if (refreshList) {
      yield dispatch(actions.fetchSiteUserQueries({ clinicalTrialId, siteId }));
    }
    yield call(runToast, 'success', 'Query updated successfully', 'Success');
  } catch (error) {
    yield* showErrorToast(error, 'Failed to update query');
    yield dispatch(actions.updateCurrentQueryFailure(error as Error));
  }
}

export function* siteUserQueriesSaga() {
  yield takeLatest(
    actions.fetchSiteUserQueries.type,
    fetchSiteUserQueriesWorker
  );
  yield takeLatest(actions.fetchCurrentQuery.type, fetchCurrentQueryWorker);
  yield takeLatest(actions.updateCurrentQuery.type, updateCurrentQueryWorker);
}
